import { addDays, format } from "date-fns";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { GeoContext } from "./GeoProvider";
import EDD from "@constants/EDD";

const defaultShippingDelayContext = {
  etaLatestDeliveryDate: null,
  deliveryDateFormatted: null,
  deliveryTimelinesFormatted: null,
  isShippingEstimationLoading: true,
};
export const ShippingDelayContext = createContext(defaultShippingDelayContext);

function formatDeliveryDate(date) {
  const day = format(date, "E");
  const month = format(date, "MMMM");
  const dateNumber = format(date, "do");
  return `${day}, ${month} ${dateNumber}`;
}

function getEDD(countryCode, state) {
  const country = EDD[countryCode] || EDD.RoW;

  if (state && country.states && country.states[state]) {
    return country.states[state];
  }

  return {
    earliest: country.earliest,
    latest: country.latest,
  };
}

function getEDDRange(countryCode, state) {
  const { earliest, latest } = getEDD(countryCode, state);
  const today = new Date();

  return {
    earliest: format(addDays(today, earliest), "yyyy-MM-dd"),
    latest: format(addDays(today, latest), "yyyy-MM-dd"),
  };
}

function ShippingDelayProvider({ children }) {
  const [etaLatestDeliveryDate, setETALatestDeliveryDate] = useState(
    defaultShippingDelayContext.etaLatestDeliveryDate
  );
  const [deliveryDateFormatted, setDeliveryDateFormatted] = useState(
    defaultShippingDelayContext.deliveryDateFormatted
  );
  const [deliveryTimelinesFormatted, setDeliveryTimelines] = useState(
    defaultShippingDelayContext.deliveryTimelinesFormatted
  );
  const [isShippingEstimationLoading, setIsShippingEstimationLoading] = useState(true);
  const { gePriceDetails, isLoadingCountry } = useContext(GeoContext);
  const countryCode = gePriceDetails?.CountryCode3;
  const countryCode2 = gePriceDetails?.CountryCode;
  const stateCode = gePriceDetails?.Subdivision || null;

  useEffect(() => {
    if (isLoadingCountry) return;
    setIsShippingEstimationLoading(true);
    const prediction = getEDDRange(countryCode2, stateCode);
    if (prediction) {
      const latest = new Date(prediction.earliest);
      setDeliveryTimelines(
        `${format(new Date(prediction.earliest), "MM/dd")} - ${format(
          new Date(prediction.latest),
          "MM/dd"
        )}`
      );
      const formattedDate = formatDeliveryDate(latest);
      setDeliveryDateFormatted(formattedDate); // Set the formatted date
      setETALatestDeliveryDate(latest);
    }
    setIsShippingEstimationLoading(false);
  }, [countryCode, isLoadingCountry]);

  const state = useMemo(
    () => ({
      etaLatestDeliveryDate,
      deliveryDateFormatted,
      deliveryTimelinesFormatted,
      isShippingEstimationLoading,
    }),
    [
      etaLatestDeliveryDate,
      deliveryDateFormatted,
      deliveryTimelinesFormatted,
      isShippingEstimationLoading,
    ]
  );

  return <ShippingDelayContext.Provider value={state}>{children}</ShippingDelayContext.Provider>;
}

export default ShippingDelayProvider;
